import { z } from 'zod';

const axisSchema = z.object({ lowLabel: z.string(), highLabel: z.string() });

const brandPositionSchema = z.object({
  advertiser: z.string(),
  /** Scored -1..1 against `axisX`. */
  x: z.number(),
  /** Scored -1..1 against `axisY`. */
  y: z.number(),
  analysis: z.string(),
});

const brandContentSchema = z.object({
  advertiser: z.string(),
  positioningSentence: z.string(),
  tactics: z.string(),
  persona: z.string(),
  funnel: z.object({ upper: z.string(), lower: z.string() }),
});

const creativeLandscapeSchema = z.object({
  positioning: z.object({
    axisX: axisSchema,
    axisY: axisSchema,
    brands: z.array(brandPositionSchema),
  }),
  brands: z.array(brandContentSchema),
  executiveSummary: z.array(z.object({ advertiser: z.string(), persona: z.string() })),
  analyzedAdvertisers: z.array(z.string()),
});

const creativeLandscapeDataSchema = z.object({
  brandName: z.string(),
  availableAdvertisers: z.array(z.string()),
  dateRange: z.object({ start: z.string().nullable(), end: z.string().nullable() }),
  // Null when the creative insights agent failed; the section is non-fatal on the backend.
  creativeLandscape: creativeLandscapeSchema.nullable(),
  // Read only to rank brands by spend, which keeps a brand's colour identical to the one it has
  // on Media Landscape. Defaulted so a gap on the media side can never blank this page.
  mediaLandscape: z
    .object({
      monthly: z
        .array(z.object({ advertiser: z.string(), spend: z.number() }))
        .catch([])
        .default([]),
    })
    .catch({ monthly: [] })
    .default({ monthly: [] }),
});

export type CreativeAxis = z.infer<typeof axisSchema>;
export type CreativeBrandPosition = z.infer<typeof brandPositionSchema>;
export type CreativeBrandContent = z.infer<typeof brandContentSchema>;
export type CreativeLandscape = z.infer<typeof creativeLandscapeSchema>;
export type CreativeLandscapeData = z.infer<typeof creativeLandscapeDataSchema>;

export type CreativeLandscapeParseResult =
  | { status: 'absent' }
  | { status: 'invalid' }
  | { status: 'ok'; data: CreativeLandscapeData };

/**
 * `pathmatics_data` is unconstrained jsonb, so a blob written before a shape change can still
 * violate the current contract at runtime. Parsing at the boundary keeps a bad upload from
 * throwing inside the page and white-screening the whole analysis.
 *
 * Only the fields this page renders are declared — `whiteSpace` and the media-side aggregates
 * are deliberately absent. Requiring one the page never reads would turn a harmless gap in an
 * older blob into a blank section, which is the failure this parse exists to prevent; zod
 * strips the rest either way.
 */
export function parseCreativeLandscapeData(raw: unknown): CreativeLandscapeParseResult {
  if (raw === null || raw === undefined) return { status: 'absent' };
  const parsed = creativeLandscapeDataSchema.safeParse(raw);
  return parsed.success ? { status: 'ok', data: parsed.data } : { status: 'invalid' };
}
